import { useState, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import ModalShell from './ModalShell';

/**
 * Potvrzení nevratné akce přes `ModalShell` — náhrada `window.confirm`.
 *
 * `window.confirm` na mobilu vyskočí jako systémový dialog mimo aplikaci
 * (v PWA i s adresou webu v titulku), nejde nastylovat a nejde z něj poznat,
 * co se vlastně stane. Hook vrací `potvrdit()` se stejným tvarem použití
 * jako `confirm` (jen asynchronně) a prvek, který se vloží do stránky:
 *
 * ```jsx
 * const [potvrdit, potvrzeni] = usePotvrzeni(isDark);
 * const smazat = async () => { if (!(await potvrdit('Smazat sekci?', { potvrdit: 'Smazat' }))) return; … };
 * return <>{…}{potvrzeni}</>;
 * ```
 *
 * @param {boolean} isDark
 * @returns {[(nadpis: string, volby?: {text?: string, potvrdit?: string, nebezpecne?: boolean}) => Promise<boolean>, React.ReactNode]}
 */
export default function usePotvrzeni(isDark) {
    const [dotaz, setDotaz] = useState(null);   // { nadpis, text, potvrdit, nebezpecne, resolve }

    const potvrdit = useCallback((nadpis, volby = {}) => new Promise((resolve) => {
        setDotaz({ nadpis, text: volby.text || null, potvrdit: volby.potvrdit || 'Potvrdit', nebezpecne: volby.nebezpecne !== false, resolve });
    }), []);

    const odpovez = (ano) => {
        dotaz?.resolve(ano);
        setDotaz(null);
    };

    const barva = dotaz?.nebezpecne ? '#EF4444' : '#B47AEA';
    const prvek = dotaz ? (
        <ModalShell onClose={() => odpovez(false)} isDark={isDark} nadpis={dotaz.nadpis} ikona={AlertTriangle} barva={barva} sirka={440}
            paticka={(
                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => odpovez(false)} className="rounded-lg"
                        style={{
                            height: 40, padding: '0 16px', fontSize: 13, cursor: 'pointer', background: 'transparent',
                            color: 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                        }}>
                        Zrušit
                    </button>
                    <button type="button" onClick={() => odpovez(true)} className="rounded-lg font-semibold"
                        style={{ height: 40, padding: '0 16px', fontSize: 13, cursor: 'pointer', background: barva, color: '#fff', border: 'none' }}>
                        {dotaz.potvrdit}
                    </button>
                </div>
            )}>
            {dotaz.text && <p className="text-sm whitespace-pre-line" style={{ padding: 16, color: 'var(--text-secondary)' }}>{dotaz.text}</p>}
        </ModalShell>
    ) : null;

    return [potvrdit, prvek];
}
